import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { StarIcon, CheckBadgeIcon } from "@heroicons/react/24/solid";
import { StarIcon as StarOutline, PencilSquareIcon } from "@heroicons/react/24/outline";

// One review composer per delivered, not-yet-reviewed product.
const ReviewCard = ({ item, onDone }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (rating < 1) { toast.error("Please pick a star rating"); return; }
    setBusy(true);
    try {
      await api.post("/reviews", { product_id: item.product_id, rating, comment });
      toast.success("Thanks for your review!");
      onDone(item.product_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit review");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)]" data-testid={`review-pending-${item.product_id}`}>
      <div className="flex gap-3">
        <div className="w-16 h-16 rounded-xl bg-muted overflow-hidden shrink-0 flex items-center justify-center">
          {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : <StarOutline className="w-6 h-6 text-muted-foreground" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm truncate">{item.title}</p>
          <p className="text-xs text-muted-foreground">Delivered · Order #{item.order_id?.slice(-8)}</p>
          <div className="flex items-center gap-1 mt-2" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onMouseEnter={() => setHover(n)} onClick={() => setRating(n)}
                data-testid={`review-star-${item.product_id}-${n}`} aria-label={`${n} star`}>
                {(hover || rating) >= n
                  ? <StarIcon className="w-6 h-6 text-amber-400" />
                  : <StarOutline className="w-6 h-6 text-muted-foreground" />}
              </button>
            ))}
          </div>
        </div>
      </div>
      <Textarea
        data-testid={`review-comment-${item.product_id}`}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Share what you liked (optional)"
        className="mt-3 rounded-xl text-sm"
        rows={2}
      />
      <Button data-testid={`review-submit-${item.product_id}`} onClick={submit} disabled={busy}
        className="mt-3 rounded-full bg-orange-500 hover:bg-orange-600">
        {busy ? "Submitting…" : "Submit review"}
      </Button>
    </Card>
  );
};

export const BuyerReviews = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/buyer/reviews-pending")
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const remove = (pid) => setItems((prev) => prev.filter((i) => i.product_id !== pid));

  if (loading) return <div className="h-40 rounded-2xl bg-muted/40 animate-pulse" />;

  if (items.length === 0) {
    return (
      <Card className="p-10 text-center rounded-2xl" data-testid="buyer-reviews-empty">
        <CheckBadgeIcon className="w-12 h-12 mx-auto mb-3 text-green-500" />
        <p className="font-semibold">You're all caught up!</p>
        <p className="text-sm text-muted-foreground mt-1">No delivered items are waiting for a review right now.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="buyer-reviews">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <PencilSquareIcon className="w-5 h-5 text-orange-500" />
        {items.length} item{items.length > 1 ? "s" : ""} waiting for your review
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((it) => <ReviewCard key={it.product_id} item={it} onDone={remove} />)}
      </div>
    </div>
  );
};
